/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useUserStore } from '@/stores/user';
import {
  authRefreshPost,
  clientClientIdGet,
  clientGetAll,
  ClientGetAllParams,
  ClientResponse,
  contactGetAll,
  ContactGetAllParams,
  ContactResponseContactType,
  courtGetAll,
  CourtGetAllParams,
  useClientGetAll,
  useContactGetAll,
  useCourtGetAll,
  userGetAll,
  UserGetAllParams,
  UserResponseSchema,
  useUserGetAll,
} from '@brainvaulttech/dgr-react-sdk';
import { axios, AxiosError, AxiosRequestConfig, setBaseUrl } from '@brainvaulttech/dgr-react-sdk/lib/client';
import axiosOriginal, { type AxiosResponse } from 'axios';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getAllServices } from './pe';
import { ServiceGetAllRequestBody } from '@/types/service.types';
import { removeNullValues } from '@/lib/utils';
import { getVendorContactsByVendorId, getVendors } from './vendorService';
import { VendorContactGetAllParams, VendorGetAllParams, VendorStatus } from '@/types/vendor.types';
import type { BillingGetAllQuerySchemaType, BillingItemBaseType } from '@/types/billing.types';
import { getBillingItems } from '@/services/invoiceService';
import type { ListPageResponse } from '@/types/types';
import { resetLogoutTimer } from '@/services/inactivityTimeOut';
import { useTeamGetAll } from './teamService';
import { TeamGetAllParams } from '@/types/team.types';

export const unAuthorizedAxios = axiosOriginal.create();

const baseLink = import.meta.env.VITE_API_URL as string;
setBaseUrl(baseLink);
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  headers: {
    Authorization: string;
  };
}

export const initializeNetwork = () => {
  const userStore = useUserStore.getState();

  axios.interceptors.response.use(
    (response) => {
      resetLogoutTimer();
      return response;
    },
    async (error: AxiosError): Promise<any> => {
      const originalRequest = error.config as CustomAxiosRequestConfig;

      if (error?.response?.status === 401 && !originalRequest?._retry && !originalRequest.url?.includes('/auth/')) {
        originalRequest._retry = true;
        try {
          const refreshTokenResponse = await authRefreshPost({ refreshToken: userStore.refreshToken! });
          const newAccessToken = refreshTokenResponse.data.tokens.accessToken;

          useUserStore.setState({ accessToken: newAccessToken });

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          resetLogoutTimer();
          return axios(originalRequest);
        } catch (refreshError) {
          userStore.reset();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  axios.interceptors.request.use((req) => {
    const currentAccessToken = useUserStore.getState().accessToken;
    req.headers = req.headers || {};
    if (currentAccessToken) {
      req.headers.Authorization = `Bearer ${currentAccessToken}`;
    }
    return req;
  });
};
initializeNetwork();
export const getClients = (params: ClientGetAllParams, options: any) => {
  const query = useClientGetAll(params, options);
  const data = query.data?.data?.clients ?? [];
  const totalRows = query.data?.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const getProcesses = (body: ServiceGetAllRequestBody, options: any) => {
  const query = useQuery({
    queryFn: () => getAllServices(removeNullValues(body)),
    queryKey: ['services', options],
  });
  const data = query.data?.services ?? [];
  const totalRows = query.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const getUsers = (params: UserGetAllParams, options: any) => {
  const query = useUserGetAll(params, options);
  const data = (query.data?.data as { users: UserResponseSchema[] })?.users ?? [];
  const totalRows = (query.data?.data as { totalRows: number })?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const getAllCourts = (params: CourtGetAllParams, options: any) => {
  const query = useCourtGetAll(params, options);
  const data = query.data?.data?.courts ?? [];
  const totalRows = query.data?.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const getAllVendors = (params: VendorGetAllParams, options?: { query?: UseQueryOptions }) => {
  const query = useQuery({
    queryFn: () => getVendors(params),
    queryKey: ['vendors', params],
    ...options,
  });
  const data = query.data?.data?.vendors ?? [];
  const totalRows = query?.data?.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};

export const getClientContactsById = (params: ContactGetAllParams, options: any) => {
  const query = useContactGetAll(params, options);
  const data = query.data?.data?.contacts ?? [];
  const totalRows = query.data?.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};

export const getProcessesById = (params: UserGetAllParams, options: any) => {
  const query = useUserGetAll(params, options);
  const data = (query.data?.data as { users: UserResponseSchema[] })?.users ?? [];
  const totalRows = (query.data?.data as { totalRows: number })?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const clientSearch = (params: ClientGetAllParams, options: any, refreshKey?: number) => {
  const query = useQuery({
    queryFn: () => clientGetAll(params, options),
    queryKey: [
      'clients',
      Object.values(params)
        .map((value) => value)
        .join(', '),
      refreshKey,
    ],
    staleTime: 1000 * 60 * 5,
  });
  const data = query.data?.data?.clients ?? [];
  const values = data.map((client) => ({
    displayLabel: client.code,
    label: client.code + ' - ' + client.clientName,
    secondaryLabel: client.city + ', ' + client.state,
    value: client.clientId,
    data: client,
  }));
  return { ...query, values, refetch: query.refetch };
};

export const clientGetById = (params: string, options?: { query?: UseQueryOptions }) => {
  const query = useQuery<AxiosResponse<ClientResponse>>({
    queryFn: () => clientClientIdGet(params),
    queryKey: ['clients', params],
    staleTime: 1000 * 60 * 5,
    ...options?.query,
  });
  const rawData = query.data?.data;
  const data: ClientResponse | null =
    rawData && typeof rawData === 'object' && 'clientId' in rawData
      ? rawData
      : null;

  return { ...query, data };
};

export const clientContactSearch = (
  params: ContactGetAllParams,
  options: any,
  employeeType?: ContactResponseContactType,
  refreshKey?: number
) => {
  const query = useQuery({
    queryFn: () => contactGetAll(params, options),
    queryKey: ['contacts', params.search, params.clientId, refreshKey],
    staleTime: 1000 * 60 * 5,
    enabled: !!params.clientId,
  });

  let data = query.data?.data.contacts ?? [];
  if (employeeType) {
    data = data.filter((contact) => contact.type === employeeType);
  }
  const values = data.map((contact) => ({
    label: contact.firstName + ' ' + contact.lastName,
    value: contact.contactId ?? '',
    option: contact,
  }));
  return { ...query, values };
};

export const courtSearch = (params: CourtGetAllParams, options: any, refreshKey?: number) => {
  const query = useQuery({
    queryFn: () => courtGetAll(params, options),
    queryKey: ['courts', params.search, refreshKey],
    staleTime: 1000 * 60 * 5,
  });

  const data = query.data?.data.courts ?? [];
  const values = data.map((court) => ({
    label: `${court.code} - ${court.name}`,
    value: court.courtId,
  }));
  return { ...query, values };
};

export const serverSearch = (params: UserGetAllParams, options: any) => {
  const query = useQuery({
    queryFn: () => userGetAll({ ...params, roleId: 'SERVER', pageSize: 100 }, options),
    queryKey: ['servers', params.search],
    staleTime: 1000 * 60 * 5,
  });
  const data =
    (query.data?.data as { users: UserResponseSchema[] })?.users?.filter((user) => user.status === 'ACTIVE') ?? [];
  const values = data.map((server) => ({
    label: server?.firstName + ' ' + server?.lastName,
    value: server.userId,
  }));
  return { ...query, values };
};

export const vendorSearch = (
  params: VendorGetAllParams,
  options?: { query?: UseQueryOptions },
  vendorId?: string | null
) => {
  const query = useQuery({
    queryFn: async () => {
      const sized: VendorGetAllParams = {
        ...params,
        pageSize: params.pageSize ?? 100,
      };
      const hadCoverage = Boolean(typeof sized.coverageArea === 'string' && sized.coverageArea.trim().length > 0);
      let response = await getVendors(sized);
      const firstCount = response.data?.vendors?.length ?? 0;
      if (hadCoverage && firstCount === 0) {
        const withoutCoverage: VendorGetAllParams = { ...sized };
        delete withoutCoverage.coverageArea;
        response = await getVendors(withoutCoverage);
      }
      return response;
    },
    queryKey: ['vendors', JSON.stringify(params), vendorId ?? ''],
    staleTime: 1000 * 60 * 5,
    ...options?.query,
  });
  const data = query.data?.data.vendors ?? [];
  const filteredData = data.filter((vendor) => vendor.status === VendorStatus.ACTIVE || vendor?.vendorId === vendorId);
  const values = filteredData.map((vendor) => ({
    label: vendor.name,
    value: vendor.vendorId,
  }));
  return { ...query, values, refetch: query.refetch };
};

export const vendorContactSearch = (
  params: VendorContactGetAllParams,
  vendorId?: string | null,
  options?: { query?: UseQueryOptions }
) => {
  const query = useQuery({
    queryFn: () => getVendorContactsByVendorId(vendorId, params),
    queryKey: ['contacts', params.search, vendorId],
    enabled: !!vendorId,
    staleTime: 1000 * 60 * 5,
    ...options?.query,
  });

  const data = query.data?.data ?? [];
  const values = data
    .filter((contact) => contact.vendorContactId !== undefined)
    .map((contact) => {
      const fromContactName = contact.contactName?.trim();
      const fromFirstLast = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
      const label = fromContactName ? fromContactName : fromFirstLast.length > 0 ? fromFirstLast : contact.email;
      return {
        label,
        value: contact.vendorContactId!,
      };
    });
  return { ...query, values };
};

export const billingItemSearch = (params: BillingGetAllQuerySchemaType, options: any) => {
  const query = useQuery<AxiosResponse<ListPageResponse<'billingItems', BillingItemBaseType>>>({
    queryFn: () => getBillingItems(params),
    queryKey: ['billing-items', Object.values(params).join(', ')],
    staleTime: 1000 * 60 * 5,
    ...options,
  });

  const values =
    query.data?.data?.billingItems?.map((item: BillingItemBaseType) => ({
      value: item.itemId ?? '',
      label: `${item.itemId} - ${item.description}`,
      option: item,
    })) ?? [];
  return { ...query, values };
};

export const teamSearch = (params: TeamGetAllParams) => {
  const normalizedParams = { ...params };
  const teamIdCandidate = normalizedParams.teamId?.trim();
  const isUuid = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  // FormFieldSearch can pass typed text in teamId; API expects teamId as UUID.
  if (teamIdCandidate) {
    if (isUuid(teamIdCandidate)) {
      normalizedParams.teamId = teamIdCandidate;
    } else {
      normalizedParams.search = teamIdCandidate;
      delete normalizedParams.teamId;
    }
  }

  const query = useTeamGetAll(normalizedParams);
  const data = query.data?.teams ?? [];

  const filteredData = data.filter((team) => !['ADMIN', 'PROCESS_SERVER'].includes(team.key));

  const values = filteredData?.map((team) => ({
    label: team.name,
    value: team.id,
  }));

  return { ...query, values };
};

export const getUsersByTeam = (params: TeamGetAllParams, isSupervisorOrAdmin: boolean) => {
  const query = useTeamGetAll(params);
  const currentUserId = useUserStore.getState().user?.userId;
  const teams = query.data?.teams ?? [];

  const allUsers = teams.flatMap(
    (team) =>
      team?.users?.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.userId,
      })) ?? []
  );

  const values = isSupervisorOrAdmin ? allUsers : allUsers.filter((user) => user.value === currentUserId);

  return { ...query, values };
};
